/**
 * Respostas rápidas do atendimento WhatsApp (Fase 2): atalho → texto que o
 * atendente insere na caixa de resposta. Gravação só pelo contrato HTTP
 * (`/api/channels/quick-replies`); nada aqui é enviado ao cliente sozinho.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { useState, type ReactElement } from "react";
import { Zap } from "lucide-react";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { Badge, Button, EmptyState, Field, PageHeader, controlClass } from "../ui/index.js";
import { useQuickReplies, useQuickReplyActions } from "../queries.js";
import type { QuickReply } from "../api/types.js";

const ERROR_LABELS: Record<string, string> = {
  invalid_shortcut: "Atalho inválido: use até 32 letras minúsculas, números, - ou _.",
  invalid_title: "Informe um título de até 80 caracteres.",
  invalid_body: "Informe o texto (até 4000 caracteres).",
  shortcut_already_exists: "Já existe uma resposta com esse atalho."
};

function friendlyError(error: Error): string {
  const key = Object.keys(ERROR_LABELS).find((code) => error.message.startsWith(code));
  return key ? ERROR_LABELS[key]! : errorDetail(error);
}

export function RespostasRapidasView({ replies }: { replies: QuickReply[] }): ReactElement {
  const { create, update, remove } = useQuickReplyActions();
  const [editing, setEditing] = useState<QuickReply | null>(null);
  const [shortcut, setShortcut] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const error = create.error ?? update.error ?? remove.error;
  const reset = () => { setEditing(null); setShortcut(""); setTitle(""); setBody(""); };
  return (
    <div className="page flex flex-col gap-6" data-page="respostas-rapidas">
      <PageHeader
        title="Respostas rápidas"
        description="Textos prontos que o atendente insere na conversa do WhatsApp (WAPI da TDZAIN). Nada é enviado sem o atendente confirmar."
      />
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_24rem]">
        {replies.length === 0 ? (
          <EmptyState icon={Zap} title="Nenhuma resposta rápida cadastrada" description="Cadastre a primeira ao lado; ela aparece na caixa de resposta do atendimento." />
        ) : (
          <ul className="m-0 flex list-none flex-col gap-3 p-0" aria-label="Respostas rápidas cadastradas">
            {replies.map((reply) => (
              <li
                key={reply.id}
                className="flex flex-col gap-2 rounded-panel border border-border bg-surface p-4 shadow-s1"
                data-record-id={reply.id}
                data-shortcut={reply.shortcut}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="m-0 flex items-center gap-2 text-base font-extrabold text-ink">
                    <Badge tone="brand">{`/${reply.shortcut}`}</Badge>
                    {reply.title}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditing(reply);
                        setShortcut(reply.shortcut);
                        setTitle(reply.title);
                        setBody(reply.body);
                      }}
                    >
                      Editar
                    </Button>
                    <Button size="sm" variant="danger" disabled={remove.isPending} onClick={() => remove.mutate(reply.id)}>
                      Excluir
                    </Button>
                  </div>
                </div>
                <p className="m-0 whitespace-pre-wrap rounded-control bg-surface-soft p-3 text-sm text-body">{reply.body}</p>
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1 xl:sticky xl:top-24"
          aria-label={editing ? "Editar resposta rápida" : "Nova resposta rápida"}
          onSubmit={(event) => {
            event.preventDefault();
            const input = { shortcut, title, body };
            if (editing) update.mutate({ id: editing.id, patch: input }, { onSuccess: reset });
            else create.mutate(input, { onSuccess: reset });
          }}
        >
          <h3 className="text-lg font-extrabold text-ink">{editing ? "Editar resposta" : "Nova resposta"}</h3>
          <Field label="Atalho (sem a barra)" hint="Letras minúsculas, números, - ou _ (até 32).">
            {({ id }) => <input id={id} className={controlClass} value={shortcut} onChange={(e) => setShortcut(e.target.value)} maxLength={33} required />}
          </Field>
          <Field label="Título">
            {({ id }) => <input id={id} className={controlClass} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} required />}
          </Field>
          <Field label="Texto">
            {({ id }) => <textarea id={id} className={controlClass + " min-h-32 py-2"} value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} required />}
          </Field>
          <div className="flex justify-end gap-2">
            {editing ? <Button onClick={reset}>Cancelar</Button> : null}
            <Button type="submit" variant="primary" disabled={create.isPending || update.isPending}>
              {editing ? "Salvar" : "Adicionar"}
            </Button>
          </div>
          {error ? <p className="m-0 text-sm font-bold text-danger" role="alert">{friendlyError(error)}</p> : null}
        </form>
      </div>
    </div>
  );
}

export function RespostasRapidas(): ReactElement {
  const query = useQuickReplies();
  if (query.isPending) return <StatusPanel kind="loading" title="Carregando respostas rápidas…" />;
  if (query.error) return <StatusPanel kind="error" title="Não foi possível carregar as respostas rápidas" detail={errorDetail(query.error)} />;
  return <RespostasRapidasView replies={query.data ?? []} />;
}
